/**
 * Recalls.
 *
 * Opening a recall finds every batch with the recalled number, where it sits,
 * every sale it went out on and every dispensing — and, by default, holds it
 * at every location. CenoX does not contact anybody: the actions log is where
 * the people who did record what they did.
 */

import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Siren } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, Input, Modal, Select, Spinner, Textarea } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { usePharmacy, usePharmacyWrite, type ListOf } from './data';
import { ErrorLine, StatusBadge, Table, Td, Tile } from './parts';
import { Checkbox, ProductPicker, StockTabs } from './MedicinesPage';

interface RecallSummary {
  id: string;
  reference: string;
  product_name: string;
  batch_numbers: string[];
  status: string;
  opened_at: string;
  closed_at: string | null;
  reason: string;
}

interface RecallImpact {
  id: string;
  reference: string;
  product_name: string;
  batch_numbers: string[];
  reason: string;
  authority_reference: string | null;
  status: string;
  opened_at: string;
  opened_by: string | null;
  closed_at: string | null;
  close_note: string | null;
  batches: Array<{ id: string; batch_number: string; expiry_date: string }>;
  stock: Array<{ batchId: string; batchNumber: string; branchId: string; branchName: string | null; onHand: number; held: boolean; holdKinds: string[] }>;
  onHandTotal: number;
  sales: Array<{ sale_id: string; reference: string; occurred_at: string; branchName: string | null; batch_number: string; base_quantity: number; customer_name: string | null; customer_phone: string | null }>;
  dispensings: Array<{ id: string; dispensed_at: string; base_quantity: number; prescription_reference?: string; patient_name?: string; patient_phone?: string | null; restricted?: boolean }>;
  dispensingCount: number;
  actions: Array<{ action: string; quantity: number | null; note: string | null; recorded_at: string; recorded_by: string | null }>;
  messagesSent: number;
}

const ACTIONS = [
  { value: 'stock_quarantined', label: 'Stock quarantined' },
  { value: 'returned_to_supplier', label: 'Returned to supplier' },
  { value: 'destroyed', label: 'Destroyed' },
  { value: 'customer_contacted', label: 'Customer or patient contacted' },
  { value: 'prescriber_contacted', label: 'Prescriber contacted' },
  { value: 'regulator_notified', label: 'Regulator notified' },
  { value: 'note', label: 'Note' },
];

const NO_MESSAGES = 'CenoX does not message patients or customers automatically. Record here any contact you made.';

export function RecallsPage(): ReactNode {
  const { can } = useAuth();
  const recalls = usePharmacy<ListOf<RecallSummary>>('/recalls', {}, can('recalls:manage'));
  const [opening, setOpening] = useState(false);

  return (
    <div>
      <PageHeader
        title="Medicines & Stock"
        description="Batch recalls: what is affected, where it is, who received it, and what was done"
        action={can('recalls:manage') ? <Button icon={<Plus className="size-4" />} onClick={() => setOpening(true)}>Open a recall</Button> : undefined}
      />
      <StockTabs />
      <p className="mb-4 text-sm text-slate-500">{NO_MESSAGES}</p>
      <Card padded={false}>
        {!can('recalls:manage') ? (
          <p className="p-5 text-sm text-slate-500">Recalls are managed by a pharmacist or the owner.</p>
        ) : recalls.isError ? (
          <div className="p-4"><ErrorState message="Could not load recalls." onRetry={() => void recalls.refetch()} /></div>
        ) : !recalls.data ? (
          <Spinner />
        ) : recalls.data.data.length === 0 ? (
          <EmptyState icon={<Siren className="size-8" />} title="No recalls" />
        ) : (
          <Table head={['Recall', 'Item', 'Batches', 'Reason', 'Opened', 'Status']} minWidth={760}>
            {recalls.data.data.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <Td><Link to={`/pharmacy/stock/recalls/${r.id}`} className="font-medium text-brand-700 hover:underline">{r.reference}</Link></Td>
                <Td>{r.product_name}</Td>
                <Td className="font-mono text-xs">{r.batch_numbers.join(', ')}</Td>
                <Td className="max-w-xs truncate">{r.reason}</Td>
                <Td className="tabular-nums">{r.opened_at.slice(0, 10)}</Td>
                <Td><StatusBadge status={r.status} /></Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
      {opening && <OpenRecallModal onClose={() => setOpening(false)} />}
    </div>
  );
}

function OpenRecallModal({ onClose }: { onClose: () => void }): ReactNode {
  const save = usePharmacyWrite<Record<string, unknown>, { id: string }>('post', '/recalls');
  const [product, setProduct] = useState<{ id: string; name: string } | null>(null);
  const [numbers, setNumbers] = useState('');
  const [reason, setReason] = useState('');
  const [authority, setAuthority] = useState('');
  const [quarantine, setQuarantine] = useState(true);
  const [opened, setOpened] = useState<string | null>(null);
  const batchNumbers = numbers.split(',').map((n) => n.trim()).filter(Boolean);
  return (
    <Modal open onClose={onClose} title="Open a recall">
      <div className="space-y-3">
        <ProductPicker value={product} onChange={setProduct} />
        <Input label="Batch numbers" hint="Separate several with commas" value={numbers} onChange={(e) => setNumbers(e.target.value)} />
        <Textarea label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
        <Input label="Authority reference" hint="e.g. the NAFDAC or manufacturer notice number" value={authority} onChange={(e) => setAuthority(e.target.value)} />
        <Checkbox label="Hold these batches at every location now" hint="Recommended. Held stock cannot be sold." checked={quarantine} onChange={setQuarantine} />
        <ErrorLine error={save.error} />
        {opened ? (
          <div className="flex justify-end">
            <Link to={`/pharmacy/stock/recalls/${opened}`} className="text-sm font-medium text-brand-700 hover:underline">See what it affects →</Link>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button
              disabled={!product || batchNumbers.length === 0 || reason.trim().length < 3}
              loading={save.isPending}
              onClick={() =>
                save.mutate(
                  { productId: product!.id, batchNumbers, reason: reason.trim(), authorityReference: authority.trim() || null, quarantine },
                  { onSuccess: (r) => setOpened(r.id) },
                )
              }
            >
              Open recall
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

export function RecallDetailPage(): ReactNode {
  const { id = '' } = useParams();
  const recall = usePharmacy<RecallImpact>(`/recalls/${id}`);
  const act = usePharmacyWrite<Record<string, unknown>>('post', `/recalls/${id}/actions`);
  const close = usePharmacyWrite<{ note: string }>('post', `/recalls/${id}/close`);
  const [action, setAction] = useState('stock_quarantined');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');
  const [closing, setClosing] = useState(false);
  const [closeNote, setCloseNote] = useState('');

  if (recall.isError) return <ErrorState message="Could not load this recall." onRetry={() => void recall.refetch()} />;
  if (!recall.data) return <Spinner />;
  const r = recall.data;
  const open = r.status === 'open';

  return (
    <div className="space-y-5">
      <Link to="/pharmacy/stock/recalls" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="size-4" aria-hidden /> Recalls
      </Link>
      <PageHeader
        title={`Recall ${r.reference}`}
        description={`${r.product_name} · batch ${r.batch_numbers.join(', ')}`}
        action={open ? <Button variant="secondary" onClick={() => setClosing(true)}>Close recall</Button> : <StatusBadge status={r.status} />}
      />
      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{NO_MESSAGES}</p>
      <Card>
        <p className="text-sm text-slate-700">{r.reason}</p>
        <p className="mt-1 text-xs text-slate-500">
          Opened {r.opened_at.slice(0, 10)} by {r.opened_by ?? '—'}
          {r.authority_reference && ` · Reference ${r.authority_reference}`}
          {r.closed_at && ` · Closed ${r.closed_at.slice(0, 10)}: ${r.close_note ?? ''}`}
        </p>
      </Card>

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Batch rows" value={r.batches.length} />
        <Tile label="Still on hand" value={r.onHandTotal} sub="base units, all locations" tone={r.onHandTotal > 0 ? 'warn' : 'plain'} />
        <Tile label="Sales" value={r.sales.length} sub="that drew from these batches" />
        <Tile label="Dispensings" value={r.dispensingCount} sub="against prescriptions" />
      </section>

      <Card padded={false}>
        <div className="p-4 sm:p-5"><CardHeader title="Stock by location" /></div>
        {r.stock.length === 0 ? (
          <p className="px-5 pb-6 text-sm text-slate-500">None of these batches is on hand anywhere.</p>
        ) : (
          <Table head={['Batch', 'Location', 'On hand', 'Hold']}>
            {r.stock.map((s) => (
              <tr key={`${s.batchId}-${s.branchId}`}>
                <Td className="font-mono text-xs">{s.batchNumber}</Td>
                <Td>{s.branchName ?? '—'}</Td>
                <Td className="tabular-nums">{s.onHand}</Td>
                <Td>{s.held ? s.holdKinds.map((k) => <Badge key={k} tone="danger">{k}</Badge>) : <Badge tone="warning">not held</Badge>}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card padded={false}>
        <div className="p-4 sm:p-5"><CardHeader title="Sales" subtitle="Customers named on the sale, where there was one" /></div>
        {r.sales.length === 0 ? (
          <p className="px-5 pb-6 text-sm text-slate-500">No sales drew from these batches.</p>
        ) : (
          <Table head={['Sale', 'When', 'Location', 'Batch', 'Qty', 'Customer']} minWidth={720}>
            {r.sales.map((s, i) => (
              <tr key={`${s.sale_id}-${i}`}>
                <Td className="font-mono text-xs">{s.reference}</Td>
                <Td className="tabular-nums">{s.occurred_at.slice(0, 16).replace('T', ' ')}</Td>
                <Td>{s.branchName ?? '—'}</Td>
                <Td className="font-mono text-xs">{s.batch_number}</Td>
                <Td className="tabular-nums">{s.base_quantity}</Td>
                <Td>{s.customer_name ? `${s.customer_name}${s.customer_phone ? ` · ${s.customer_phone}` : ''}` : 'Walk-in'}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card padded={false}>
        <div className="p-4 sm:p-5"><CardHeader title="Dispensings" subtitle="Patient details are shown only to those allowed to see patients" /></div>
        {r.dispensings.length === 0 ? (
          <p className="px-5 pb-6 text-sm text-slate-500">Nothing was dispensed from these batches.</p>
        ) : (
          <Table head={['When', 'Prescription', 'Patient', 'Qty']}>
            {r.dispensings.map((d) => (
              <tr key={d.id}>
                <Td className="tabular-nums">{d.dispensed_at.slice(0, 16).replace('T', ' ')}</Td>
                <Td className="font-mono text-xs">{d.prescription_reference ?? '—'}</Td>
                <Td>{d.restricted || !d.patient_name ? <span className="text-slate-400">Restricted</span> : `${d.patient_name}${d.patient_phone ? ` · ${d.patient_phone}` : ''}`}</Td>
                <Td className="tabular-nums">{d.base_quantity}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card padded={false}>
        <div className="p-4 sm:p-5"><CardHeader title="Actions taken" /></div>
        {r.actions.length > 0 && (
          <Table head={['When', 'Action', 'Qty', 'Note', 'By']}>
            {r.actions.map((a, i) => (
              <tr key={i}>
                <Td className="tabular-nums">{a.recorded_at.slice(0, 16).replace('T', ' ')}</Td>
                <Td>{ACTIONS.find((x) => x.value === a.action)?.label ?? a.action}</Td>
                <Td className="tabular-nums">{a.quantity ?? '—'}</Td>
                <Td className="max-w-sm">{a.note}</Td>
                <Td>{a.recorded_by}</Td>
              </tr>
            ))}
          </Table>
        )}
        {open && (
          <div className="grid gap-3 p-4 sm:grid-cols-[1fr_8rem] sm:p-5">
            <Select label="Record an action" value={action} onChange={(e) => setAction(e.target.value)} options={ACTIONS} />
            <Input label="Quantity" inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            <div className="sm:col-span-2">
              <Textarea label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <ErrorLine error={act.error} />
              <Button
                className="mt-2"
                loading={act.isPending}
                disabled={quantity !== '' && !/^\d+$/.test(quantity)}
                onClick={() =>
                  act.mutate(
                    { action, quantity: quantity === '' ? null : Number(quantity), note: note.trim() || null },
                    { onSuccess: () => { setNote(''); setQuantity(''); } },
                  )
                }
              >
                Record action
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Modal open={closing} onClose={() => setClosing(false)} title="Close this recall">
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Closing allows any remaining recall holds to be decided. It does not release stock by itself.</p>
          <Textarea label="Outcome" required value={closeNote} onChange={(e) => setCloseNote(e.target.value)} />
          <ErrorLine error={close.error} />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setClosing(false)}>Cancel</Button>
            <Button disabled={closeNote.trim().length < 3} loading={close.isPending} onClick={() => close.mutate({ note: closeNote.trim() }, { onSuccess: () => setClosing(false) })}>
              Close recall
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
